import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface RubberTubeProps {
  /** World start point of the tube. */
  fromPos: [number, number, number];
  /** World end point of the tube. */
  toPos: [number, number, number];
  /** Optional midpoint offset added to the straight-line midpoint to bow the tube up/sideways. */
  midOffset?: [number, number, number];
  /** Whether to render the tube at all. */
  visible?: boolean;
  /** 0..1 — how much of the tube is drawn (animates the "connect" step). */
  progress?: number;
  /** Tube radius. Default 0.0065. */
  radius?: number;
  /** Hex colour for the rubber material. Default dark red. */
  color?: string;
}

/**
 * Flexible rubber tubing — a thin red rubber tube that connects two
 * apparatus pieces along a CatmullRom curve. Used in the CuO+H₂
 * experiment to connect (a) the gas-generator outlet to the glass tube,
 * and (b) the glass tube to the water-safety bottle inlet.
 *
 * The tube can animate "drawing itself" between the two endpoints by
 * setting `progress` from 0 → 1 (controlled by the store's
 * `connectProgress` field).
 */
export function RubberTube({
  fromPos,
  toPos,
  midOffset = [0, 0.25, 0],
  visible = true,
  progress = 1,
  radius = 0.0065,
  color = '#8b1e1e',
}: RubberTubeProps) {
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(visible ? 1 : 0);

  useFrame(() => {
    if (!groupRef.current) return;
    scaleRef.current += ((visible ? 1 : 0) - scaleRef.current) * 0.12;
    groupRef.current.scale.setScalar(scaleRef.current);
  });

  const curve = useMemo(() => {
    const a = new THREE.Vector3(...fromPos);
    const b = new THREE.Vector3(...toPos);
    const mid = a.clone().lerp(b, 0.5).add(new THREE.Vector3(...midOffset));
    return new THREE.CatmullRomCurve3([a, mid, b], false, 'catmullrom', 0.5);
  }, [fromPos, toPos, midOffset]);

  // For animated "drawing" effect, render only the first `progress` portion
  // of the curve by sub-sampling.
  const tubeCurve = useMemo(() => {
    const segments = 30;
    const cut = Math.max(0.001, Math.min(1, progress));
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * cut;
      pts.push(curve.getPoint(t));
    }
    return new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
  }, [curve, progress]);

  return (
    <group ref={groupRef} scale={0}>
      <mesh castShadow receiveShadow>
        <tubeGeometry args={[tubeCurve, 36, radius, 12, false]} />
        <meshStandardMaterial color={color} roughness={0.85} metalness={0.02} />
      </mesh>
      {/* Faint highlight stripe along the tube for visual interest */}
      <mesh>
        <tubeGeometry args={[tubeCurve, 36, radius * 0.45, 8, false]} />
        <meshStandardMaterial color="#a83a3a" roughness={0.55} metalness={0.05} transparent opacity={0.5} />
      </mesh>
    </group>
  );
}

export default RubberTube;
